import { defineStore } from "pinia";
import {
  type userType,
  store,
  router,
  resetRouter,
  routerArrays,
  storageLocal
} from "../utils";
import {
  type UserResult,
  type RefreshTokenResult,
  getLogin,
  refreshTokenApi,
  getUserConstInfo,
  loginByPassword,
  wxUserLogin,
  wxUserBindPhone
} from "@/api/user";
import { useAppStoreHook } from "./app";
import { useMultiTagsStoreHook } from "./multiTags";
import {
  type DataInfo,
  setToken,
  removeToken,
  userKey,
  getFullToken
} from "@/utils/auth";
import { useLiveRoomStore } from "./liveRoom";
import { emitter } from "@/utils/emitter";

export const useUserStore = defineStore("pure-user", {
  state: (): userType => ({
    // 头像
    avatar: storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "",
    // 用户名
    username: storageLocal().getItem<DataInfo<number>>(userKey)?.username ?? "",
    // 昵称
    nickname: storageLocal().getItem<DataInfo<number>>(userKey)?.nickname ?? "",
    // 页面级别权限
    roles: storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [],
    // 按钮级别权限
    permissions:
      storageLocal().getItem<DataInfo<number>>(userKey)?.permissions ?? [],
    // 前端生成的验证码（按实际需求替换）
    verifyCode: "",
    // 判断登录页面显示哪个组件（0：登录（默认）、1：手机登录、2：二维码登录、3：注册、4：忘记密码）
    currentPage: 0,
    // 是否勾选了登录页的免登录
    isRemembered: false,
    // 登录页的免登录存储几天，默认7天
    loginDay: 7,
    // 用户详细信息
    userInfo: {
      nickname: "",
      avatar: "",
      phone: "",
      gender: 0,
      email: "",
      birthday: "", // 添加生日字段
      isVip: false, // 添加会员状态字段
      realname: "", // 添加真实姓名字段
      username: "", // 添加用户名字段
      id: "" // 添加用户ID字段
    },
    // 账户余额
    balance: 0,
    // 消费记录
    consumeRecords: []
  }),
  actions: {
    /** 存储头像 */
    SET_AVATAR(avatar: string) {
      this.avatar = avatar;
    },
    /** 存储用户名 */
    SET_USERNAME(username: string) {
      this.username = username;
    },
    /** 存储昵称 */
    SET_NICKNAME(nickname: string) {
      this.nickname = nickname;
    },
    /** 存储角色 */
    SET_ROLES(roles: Array<string>) {
      this.roles = roles;
    },
    /** 存储按钮级别权限 */
    SET_PERMS(permissions: Array<string>) {
      this.permissions = permissions;
    },
    /** 存储前端生成的验证码 */
    SET_VERIFYCODE(verifyCode: string) {
      this.verifyCode = verifyCode;
    },
    /** 存储登录页面显示哪个组件 */
    SET_CURRENTPAGE(value: number) {
      this.currentPage = value;
    },
    /** 存储是否勾选了登录页的免登录 */
    SET_ISREMEMBERED(bool: boolean) {
      this.isRemembered = bool;
    },
    /** 设置登录页的免登录存储几天 */
    SET_LOGINDAY(value: number) {
      this.loginDay = Number(value);
    },
    /** 存储用户详细信息 */
    SET_USERINFO(info: any) {
      // 确保所有字段都正确保存，使用原有信息作为默认值
      this.userInfo = {
        ...this.userInfo, // 保留原有信息
        ...info // 用新信息覆盖
      };
    },
    /** 存储账户余额 */
    SET_BALANCE(amount: number) {
      this.balance = amount;
    },
    /** 存储消费记录 */
    SET_CONSUME_RECORDS(records: any[]) {
      this.consumeRecords = records;
    },
    /** 更新用户信息 */
    UPDATE_USERINFO(info: any) {
      this.userInfo = {
        ...this.userInfo,
        ...info
      };
    },
    /** 手机号登入 */
    async loginByUsername(data) {
      return new Promise<UserResult>((resolve, reject) => {
        getLogin(data)
          .then(data => {
            if (data?.success) {
              // 确保token数据结构正确
              const tokenData = {
                accessToken: data.result.token,
                expires: Date.now() + 24 * 60 * 60 * 30000, // 默认30天过期
                refreshToken: data.result.token,
                // 如果接口返回了用户信息，也存储它们
                username: data.result.username || "",
                nickname: data.result.realname || data.result.username || "",
                avatar: data.result.avatar || "",
                roles: data.result.roles || [],
                permissions: data.result.permissions || []
              };

              setToken(tokenData);
              // 登录成功后获取用户详细信息
              this.getUserInfo();
            } else {
            }
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    /** 账号密码登陆 */
    async loginByPassword(data) {
      return new Promise<UserResult>((resolve, reject) => {
        loginByPassword(data)
          .then(data => {
            if (data?.success) {
              // 确保token数据结构正确
              const tokenData = {
                accessToken: data.result.token,
                expires: Date.now() + 24 * 60 * 60 * 30000, // 默认30天过期
                refreshToken: data.result.token,
                // 如果接口返回了用户信息，也存储它们
                username: data.result.username || "",
                nickname: data.result.realname || data.result.username || "",
                avatar: data.result.avatar || "",
                roles: data.result.roles || [],
                permissions: data.result.permissions || []
              };

              setToken(tokenData);
              // 登录成功后获取用户详细信息
              this.getUserInfo();
            } else {
            }
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 账号密码登陆 */
    async loginByWx(code, cb) {
      return new Promise<UserResult>((resolve, reject) => {
        wxUserLogin({ code })
          .then(data => {
            if (data?.success && data?.result?.status !== "need_bind") {
              // 确保token数据结构正确
              const tokenData = {
                accessToken: data.result.token,
                expires: Date.now() + 24 * 60 * 60 * 30000, // 默认30天过期
                refreshToken: data.result.token,
                // 如果接口返回了用户信息，也存储它们
                username: data.result.username || "",
                nickname: data.result.realname || data.result.username || "",
                avatar: data.result.avatar || "",
                roles: data.result.roles || [],
                permissions: data.result.permissions || []
              };
              setToken(tokenData);
              // 登录成功后获取用户详细信息
              this.getUserInfo();
            } else {
              cb();
            }
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 绑定手机号码 */
    async bindMobileLoginByWx(params, cb) {
      return new Promise<UserResult>((resolve, reject) => {
        wxUserBindPhone(params)
          .then(data => {
            if (data.state !== "need_bind") {
              // 确保token数据结构正确
              const tokenData = {
                accessToken: data.result.token,
                expires: Date.now() + 24 * 60 * 60 * 30000, // 默认30天过期
                refreshToken: data.result.token,
                // 如果接口返回了用户信息，也存储它们
                username: data.result.username || "",
                nickname: data.result.realname || data.result.username || "",
                avatar: data.result.avatar || "",
                roles: data.result.roles || [],
                permissions: data.result.permissions || []
              };
              setToken(tokenData);
              // 登录成功后获取用户详细信息
              this.getUserInfo();
            } else {
              cb();
            }
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 前端登出（不调用接口） */
    logOut() {
      // 清理store状态
      this.username = "";
      this.avatar = "";
      this.nickname = "";
      this.roles = [];
      this.permissions = [];
      this.userInfo = {
        nickname: "",
        avatar: "",
        phone: "",
        gender: 0,
        email: "",
        birthday: "",
        isVip: false,
        realname: "",
        username: "",
        id: ""
      };
      this.balance = 0;
      this.consumeRecords = [];
      this.verifyCode = "";
      this.currentPage = 0;
      this.isRemembered = false;

      // 清理本地存储
      removeToken();
      storageLocal().removeItem("userDetailInfo");
      storageLocal().removeItem(userKey);

      // 清理直播房间状态
      useLiveRoomStore().roomStates.clear();

      // 清理其他相关状态
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays]);
      useAppStoreHook().toggleDevice("desktop");

      // 重置路由和跳转到登录页
      resetRouter();
      router.push("/login");
    },
    /** 清理所有用户状态 */
    clearUserState() {
      // 清理store状态
      this.username = "";
      this.avatar = "";
      this.nickname = "";
      this.roles = [];
      this.permissions = [];
      this.userInfo = {
        nickname: "",
        avatar: "",
        phone: "",
        gender: 0,
        email: "",
        birthday: "",
        isVip: false,
        realname: "",
        username: "",
        id: ""
      };
      this.balance = 0;
      this.consumeRecords = [];
      this.verifyCode = "";
      this.currentPage = 0;
      this.isRemembered = false;

      // 清理本地存储
      removeToken();
      storageLocal().removeItem("userDetailInfo");
      storageLocal().removeItem(userKey);

      // 清理直播房间状态
      useLiveRoomStore().roomStates.clear();

      // 清理其他相关状态
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays]);
      useAppStoreHook().toggleDevice("desktop");

      // 重置路由和跳转到登录页
      resetRouter();
    },
    /** 刷新`token` */
    async handRefreshToken(data: any) {
      return new Promise<RefreshTokenResult>((resolve, reject) => {
        // 模拟刷新token的实现，在真实项目中应该调用真实的刷新接口
        refreshTokenApi()
          .then(response => {
            if (response && response.success) {
              const data: RefreshTokenResult = {
                success: true,
                data: {
                  accessToken: response.data.accessToken,
                  refreshToken: response.data.refreshToken,
                  expires: new Date(response.data.expires)
                }
              };
              setToken(data.data);
              resolve(data);
            } else {
              reject(new Error("刷新token失败"));
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 获取用户详细信息 */
    async getUserInfo() {
      // 🚨 新增：检查用户是否已登录，避免在登录页面调用API
      const currentPath = window.location.pathname;
      const whiteList = ["/login", "/agreement/user", "/agreement/privacy"];
      if (whiteList.includes(currentPath)) {
        return null; // 在登录页面不获取用户信息
      }

      // 检查是否有有效token
      const fullToken = getFullToken();
      if (!fullToken || !fullToken.accessToken) {
        return null; // 没有token时不获取用户信息
      }

      // 如果已经有数据且不是强制刷新，直接返回现有数据
      if (this.userInfo.nickname && this.balance > 0) {
        return {
          userInfo: this.userInfo,
          balance: this.balance
        };
      }

      // 先尝试从本地存储恢复
      const savedInfo = storageLocal().getItem("userDetailInfo") as {
        userInfo?: any;
        balance?: number;
      } | null;
      if (savedInfo && savedInfo.userInfo && savedInfo.userInfo.nickname) {
        this.SET_USERINFO(savedInfo.userInfo);
        if (savedInfo.balance !== undefined) {
          this.SET_BALANCE(savedInfo.balance);
        }
        return savedInfo;
      }

      // 最后才从API获取
      try {
        const res = await getUserConstInfo({});
        if (res && res.success) {
          const {
            userInfo,
            balance,
            consumeRecords,
            activationExpireTime,
            maxRoomLimit,
            packageName,
            userType
          } = res.result;
          userInfo.activationExpireTime = activationExpireTime || "";
          userInfo.maxRoomLimit = maxRoomLimit;
          userInfo.packageName = packageName;
          userInfo.userType = userType;
          // 直接保存API返回的完整用户信息，不过滤字段
          this.SET_USERINFO(userInfo);
          this.SET_BALANCE(balance);
          this.SET_CONSUME_RECORDS(consumeRecords?.records || []);

          // 同步更新用户名和头像到主状态
          if (userInfo.nickname) {
            this.SET_NICKNAME(userInfo.nickname);
          }
          if (userInfo.avatar) {
            this.SET_AVATAR(userInfo.avatar);
          }

          // 保存到本地存储，以便页面刷新时恢复
          storageLocal().setItem("userDetailInfo", {
            userInfo: this.userInfo,
            balance: this.balance
          });

          // 🚀 登录成功后加载全局配置
          try {
            const { useGlobalConfigStore } = await import(
              "@/store/modules/globalConfig"
            );
            const globalConfigStore = useGlobalConfigStore();
            // 重置状态并重新加载
            globalConfigStore.isLoaded = false;
            globalConfigStore.fetchConfigs();
          } catch (configError) {}

          return res.result;
        }
        return null;
      } catch (error) {
        return null;
      }
    },

    /** 强制刷新用户信息 */
    async forceRefreshUserInfo() {
      try {
        const res = await getUserConstInfo({});
        if (res && res.success) {
          const { userInfo, balance, consumeRecords } = res.result;
          // 直接保存API返回的完整用户信息，不过滤字段
          this.SET_USERINFO(userInfo);
          this.SET_BALANCE(balance);
          this.SET_CONSUME_RECORDS(consumeRecords?.records || []);

          // 同步更新用户名和头像到主状态
          if (userInfo.nickname) {
            this.SET_NICKNAME(userInfo.nickname);
          }
          if (userInfo.avatar) {
            this.SET_AVATAR(userInfo.avatar);
          }

          // 保存到本地存储，以便页面刷新时恢复
          storageLocal().setItem("userDetailInfo", {
            userInfo: this.userInfo,
            balance: this.balance
          });

          return res.result;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    /** 从本地存储恢复用户信息 */
    restoreUserInfo() {
      const savedInfo = storageLocal().getItem("userDetailInfo") as {
        userInfo?: any;
        balance?: number;
      } | null;
      if (savedInfo) {
        if (savedInfo.userInfo) {
          this.SET_USERINFO(savedInfo.userInfo);
        }
        if (savedInfo.balance !== undefined) {
          this.SET_BALANCE(savedInfo.balance);
        }
        return true;
      }
      return false;
    },
    /** 🚀 实时更新余额（用于跨浏览器同步） */
    updateBalanceRealtime(newBalance: number) {
      this.SET_BALANCE(newBalance);

      // 同时更新本地存储，确保刷新页面后数据一致
      const savedInfo =
        (storageLocal().getItem("userDetailInfo") as {
          userInfo?: any;
          balance?: number;
        }) || {};
      storageLocal().setItem("userDetailInfo", {
        ...savedInfo,
        balance: newBalance
      });
    }
  }
});

export function useUserStoreHook() {
  return useUserStore(store);
}
